import AddSongsToPlaylistModalSWMSheet from "@components/modals/AddSongsToPlaylistModalSWM";
import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LText from "@components/LText";
import PlaylistSongSuggestionRow from "@components/Collection/playlist/PlaylistSongSuggestionRow";
import { useAddSong } from "@hooks/trpc/usePlaylists";
import { useRecommendedSongs } from "@hooks/trpc/useRecommendedSongs";
import type { Song } from "@libs/wire";
import { ActivityIndicator } from "react-native";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { toast } from "sonner-native";

const PREVIEW_SONG_LIMIT = 5;

type PlaylistRecommendationsProps = {
  existingSongIds: string[];
  playlistId: string;
};

const PlaylistRecommendations = ({
  existingSongIds,
  playlistId,
}: PlaylistRecommendationsProps) => {
  const { data: recommendations, isLoading } = useRecommendedSongs();
  const { addSong } = useAddSong();
  const bottomSheetManager = useBottomSheetManager();
  const existingSongIdSet = new Set(existingSongIds);
  const availableRecommendations = (recommendations ?? [])
    .filter(
      (song) => !existingSongIdSet.has(song.id) && song.status !== "external",
    )
    .slice(0, PREVIEW_SONG_LIMIT);

  const handleAddSong = (song: Song) => {
    addSong([playlistId], song);
    toast.success("Song added to playlist", { duration: 1500 });
  };

  const handleOpenAddSongs = () => {
    bottomSheetManager.open(
      <AddSongsToPlaylistModalSWMSheet
        existingSongIds={existingSongIds}
        playlistId={playlistId}
      />,
      { mode: "push" },
    );
  };

  return (
    <LBox px="s4" pt="m1">
      <LButton
        alignSelf="center"
        borderRadius="max"
        label="ADD SONGS"
        onPress={handleOpenAddSongs}
        px="m2"
        py="s3"
        sentryEventName="playlist_add_songs"
        testID="playlist-add-songs"
        textVariant="bodyM2Black"
      />
      <LText mt="m1" mb="s2" variant="messageTitle" fontWeight="bold">
        Recommended songs
      </LText>
      {isLoading ? (
        <LBox alignItems="center" py="m1">
          <ActivityIndicator />
        </LBox>
      ) : availableRecommendations.length > 0 ? (
        availableRecommendations.map((song) => (
          <PlaylistSongSuggestionRow
            key={song.id}
            onAdd={handleAddSong}
            song={song}
          />
        ))
      ) : (
        <LText color="greyText" pb="m1" variant="bodyM2m">
          Search for music to add to this playlist.
        </LText>
      )}
    </LBox>
  );
};

export default PlaylistRecommendations;
